import { useState } from 'react';
import { usersApi } from '../lib/api';
import { Button } from './ui/button';
import { 
    Users, 
    Plus, 
    User,
    Shield,
    Trash2,
    Power,
    AlertCircle,
    Edit,
    MessageSquare,
    Phone,
    Check
} from 'lucide-react';
import CreateUserModal from './CreateUserModal';
import EditUserModal from './EditUserModal';
import ConfirmDialog from './ConfirmDialog';

export default function UserManagement({ users, onUserCreated, onRefresh }) {
    const [showCreateUser, setShowCreateUser] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    
    // Stato per il dialog di conferma eliminazione
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [userToDelete, setUserToDelete] = useState(null);
    
    // Stato per invio SMS
    const [sendingSmsForUser, setSendingSmsForUser] = useState(null);

    const handleToggleActive = async (userId) => {
        try {
            await usersApi.toggleActive(userId);
            onRefresh();
        } catch (err) {
            setError('Errore nel modificare lo stato dell\'utente');
        }
    };

    // Apre il dialog di conferma invece di usare window.confirm
    const confirmDeleteUser = (user) => {
        setUserToDelete(user);
        setDeleteDialogOpen(true);
    };

    // Esegue effettivamente l'eliminazione
    const executeDeleteUser = async () => {
        if (!userToDelete) return;
        
        try {
            await usersApi.delete(userToDelete.id);
            setUserToDelete(null);
            onRefresh();
        } catch (err) {
            setError('Errore nell\'eliminazione dell\'utente');
        }
    };
    
    // Invia SMS di notifica (al numero fisso con CF dell'agente)
    const handleSendSms = async (agent) => {
        if (!agent.fiscal_code) {
            setError(`L'agente ${agent.full_name} non ha un codice fiscale configurato.`);
            setTimeout(() => setError(''), 5000);
            return;
        }
        
        try {
            setSendingSmsForUser(agent.id);
            setError('');
            
            await usersApi.sendSmsNotification(agent.id, '', 'Test notifica');
            
            setSuccess(`SMS inviato! (Agente: ${agent.full_name}, CF: ${agent.fiscal_code})`);
            setTimeout(() => setSuccess(''), 5000);
        } catch (err) {
            setError(err.response?.data?.detail || 'Errore nell\'invio dell\'SMS');
            setTimeout(() => setError(''), 5000);
        } finally {
            setSendingSmsForUser(null);
        }
    };

    const handleUserCreated = (newUser) => {
        onUserCreated(newUser);
        setShowCreateUser(false);
    };

    const admins = users.filter(u => u.role === 'admin');
    const agents = users.filter(u => u.role === 'agent');

    return (
        <div>
            {/* Action Bar */}
            <div className="flex justify-between items-center mb-6">
                <h2 className="font-heading text-xl font-bold text-white">
                    Gestione Utenti
                </h2>
                <Button
                    onClick={() => setShowCreateUser(true)}
                    data-testid="create-user-btn"
                    className="bg-[#00FF41] text-black hover:bg-[#00CC33] hover:shadow-neon rounded-none"
                >
                    <Plus className="w-4 h-4 mr-2" />
                    NUOVO UTENTE
                </Button>
            </div>

            {error && (
                <div className="bg-red-500/10 border border-red-500 p-4 mb-6 flex items-center gap-2">
                    <AlertCircle className="w-5 h-5 text-red-500" />
                    <span className="text-red-500">{error}</span>
                </div>
            )}
            
            {success && (
                <div className="bg-green-500/10 border border-green-500 p-4 mb-6 flex items-center gap-2">
                    <MessageSquare className="w-5 h-5 text-green-500" />
                    <span className="text-green-500">{success}</span>
                </div>
            )}

            {/* Admins Section */}
            <div className="mb-8">
                <h3 className="text-white/60 uppercase text-xs tracking-wider mb-3 flex items-center gap-2">
                    <Shield className="w-4 h-4 text-[#00FF41]" />
                    Amministratori ({admins.length})
                </h3>
                <div className="space-y-2">
                    {admins.map((admin) => (
                        <div
                            key={admin.id}
                            data-testid={`user-card-${admin.id}`}
                            className="bg-[#0A0A0A] border border-white/10 p-4 flex items-center justify-between"
                        >
                            <div className="flex items-center gap-4">
                                <div className="w-10 h-10 bg-[#00FF41]/10 border border-[#00FF41] flex items-center justify-center">
                                    <Shield className="w-5 h-5 text-[#00FF41]" />
                                </div>
                                <div>
                                    <p className="text-white font-medium">{admin.full_name}</p>
                                    <p className="text-white/60 text-sm font-mono">{admin.email}</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-2">
                                <span className={`px-2 py-1 text-xs font-mono ${
                                    admin.is_active 
                                        ? 'text-[#00FF41] border border-[#00FF41]' 
                                        : 'text-red-500 border border-red-500'
                                }`}>
                                    {admin.is_active ? 'ATTIVO' : 'DISATTIVO'}
                                </span>
                                <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => setEditingUser(admin)}
                                    data-testid={`edit-admin-${admin.id}`}
                                    className="p-2 hover:bg-white/10 text-white/60 hover:text-[#00FF41]"
                                    title="Modifica"
                                >
                                    <Edit className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Agents Section */}
            <div>
                <h3 className="text-white/60 uppercase text-xs tracking-wider mb-3 flex items-center gap-2">
                    <Users className="w-4 h-4 text-[#00FF41]" />
                    Agenti ({agents.length})
                </h3>
                {agents.length === 0 ? (
                    <div className="bg-[#0A0A0A] border border-white/10 p-8 text-center">
                        <User className="w-10 h-10 text-white/20 mx-auto mb-3" />
                        <p className="text-white/60">Nessun agente registrato</p>
                        <Button
                            onClick={() => setShowCreateUser(true)}
                            className="mt-4 bg-[#00FF41] text-black hover:bg-[#00CC33] rounded-none"
                        >
                            Aggiungi agente
                        </Button>
                    </div>
                ) : (
                    <div className="space-y-2">
                        {agents.map((agent) => (
                            <div
                                key={agent.id}
                                data-testid={`user-card-${agent.id}`}
                                className="bg-[#0A0A0A] border border-white/10 p-4 flex items-center justify-between"
                            >
                                <div className="flex items-center gap-4">
                                    <div className="w-10 h-10 bg-white/5 border border-white/20 flex items-center justify-center">
                                        <User className="w-5 h-5 text-white/60" />
                                    </div>
                                    <div>
                                        <p className="text-white font-medium">{agent.full_name}</p>
                                        <p className="text-white/60 text-sm font-mono">{agent.email}</p>
                                        {agent.phone && (
                                            <p className="text-white/40 text-xs flex items-center gap-1 mt-0.5">
                                                <Phone className="w-3 h-3" /> {agent.phone}
                                            </p>
                                        )}
                                    </div>
                                </div>
                                <div className="flex items-center gap-2">
                                    <span className={`px-2 py-1 text-xs font-mono ${
                                        agent.is_active 
                                            ? 'text-[#00FF41] border border-[#00FF41]' 
                                            : 'text-red-500 border border-red-500'
                                    }`}>
                                        {agent.is_active ? 'ATTIVO' : 'DISATTIVO'}
                                    </span>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleSendSms(agent)}
                                        disabled={sendingSmsForUser === agent.id}
                                        className="text-blue-400/60 hover:text-blue-400 hover:bg-blue-400/10"
                                        title={`Invia SMS con CF: ${agent.fiscal_code || 'N/A'}`}
                                        data-testid={`sms-user-btn-${agent.id}`}
                                    >
                                        {sendingSmsForUser === agent.id ? (
                                            <div className="w-4 h-4 border-2 border-blue-400/30 border-t-blue-400 rounded-full animate-spin" />
                                        ) : (
                                            <MessageSquare className="w-4 h-4" />
                                        )}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => setEditingUser(agent)}
                                        className="text-white/40 hover:text-[#00FF41] hover:bg-[#00FF41]/10"
                                        title="Modifica"
                                        data-testid={`edit-user-btn-${agent.id}`}
                                    >
                                        <Edit className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => handleToggleActive(agent.id)}
                                        className="text-white/40 hover:text-white hover:bg-white/10"
                                        title={agent.is_active ? 'Disattiva' : 'Attiva'}
                                        data-testid={`toggle-user-btn-${agent.id}`}
                                    >
                                        <Power className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={() => confirmDeleteUser(agent)}
                                        className="text-red-500/60 hover:text-red-500 hover:bg-red-500/10"
                                        title="Elimina"
                                        data-testid={`delete-user-btn-${agent.id}`}
                                    >
                                        <Trash2 className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            {/* Create User Modal */}
            {showCreateUser && (
                <CreateUserModal
                    onClose={() => setShowCreateUser(false)}
                    onCreated={handleUserCreated}
                />
            )}

            {/* Edit User Modal */}
            {editingUser && (
                <EditUserModal
                    user={editingUser}
                    onClose={() => setEditingUser(null)}
                    onUpdated={() => {
                        setEditingUser(null);
                        onRefresh();
                    }}
                />
            )}
            
            {/* Dialog di conferma eliminazione utente */}
            <ConfirmDialog
                open={deleteDialogOpen}
                onOpenChange={setDeleteDialogOpen}
                title="Elimina utente"
                description={userToDelete ? `Sei sicuro di voler eliminare l'utente "${userToDelete.full_name}"? Questa azione è irreversibile.` : ''}
                confirmText="Elimina"
                cancelText="Annulla"
                onConfirm={executeDeleteUser}
                variant="destructive"
            />
        </div>
    );
}
